import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { theme } from '../../styles/theme';

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  overflow: hidden;
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0.3)
  );
  z-index: 0;
`;

const StyledSlider = styled(Slider)`
  height: 100vh;

  .slick-slide {
    height: 100vh;
    transition: opacity 0.5s ease;
  }

  .slick-dots {
    bottom: 40px;
    z-index: 2;

    li button:before {
      color: white;
      font-size: 12px;
      opacity: 0.5;
      transition: all 0.3s ease;
    }

    li.slick-active button:before {
      color: ${({ theme }) => theme.colors.secondary};
      opacity: 1;
      transform: scale(1.2);
    }
  }
`;

const Slide = styled.div<{ bgImage: string }>`
  height: 100vh;
  position: relative;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3)),
    url(${({ bgImage }) => bgImage}) center/cover no-repeat;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease-in-out;

  &.slick-active {
    animation: zoomIn 6s ease forwards;
  }

  @keyframes zoomIn {
    from {
      transform: scale(1);
    }
    to {
      transform: scale(1.1);
    }
  }
`;

const SlideContent = styled.div`
  position: relative;
  text-align: center;
  color: white;
  z-index: 1;
  width: 90%;
  max-width: 1200px;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 4rem;
  margin-bottom: 2rem;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.2rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  border-radius: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-30px);
    }
    60% {
      transform: translateX(-50%) translateY(-15px);
    }
  }
`;

const VideoSlide = styled.div`
  height: 100vh;
  position: relative;
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: black;
`;

const YouTubeContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const YouTubeIframe = styled.iframe`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100vw;
  height: 56.25vw; /* maintain 16:9 aspect ratio */
  min-height: 100vh;
  min-width: 177.77vh; /* maintain 16:9 aspect ratio */
  pointer-events: none; /* prevents interaction with the video */
  border: none; /* Remove border */
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.secondary}; /* Add orange glow */
`;

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const videoRef = useRef<HTMLIFrameElement>(null);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    arrows: false,
    cssEase: "linear",
    pauseOnHover: false,
    initialSlide: 0,
    beforeChange: (oldIndex: number, newIndex: number) => {
      setCurrentSlide(newIndex);
      // If we're moving to the video slide, reset the video
      if (newIndex === 0 && videoRef.current) {
        const videoId = "_NjDxBsMk5M";
        const newUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=0&enablejsapi=1&rel=0&start=0`;
        videoRef.current.src = newUrl;
      }
    },
    afterChange: (current: number) => {
      // If we're on the video slide and it's been playing for more than 30 seconds, move to next slide
      if (current === 0 && videoRef.current) {
        setTimeout(() => {
          if (sliderRef.current) {
            sliderRef.current.slickNext();
          }
        }, 30000); // 30 seconds - 12 seconds = 18 seconds of play time
      }
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          dots: true,
          arrows: false
        }
      }
    ]
  };

  const sliderRef = useRef<Slider>(null);

  const slides = [
    {
      title: "Welcome to Jupiter & Co",
      subtitle: "Discover premium quality wood and timber solutions for your most ambitious projects.",
      image: "/images/hero/slide1.jpg",
    },
    {
      title: "Quality Craftsmanship",
      subtitle: "Expert woodworking and superior materials for lasting beauty.",
      image: "/images/hero/slide2.jpg",
    },
    {
      title: "Custom Solutions",
      subtitle: "Tailored wood products to meet your unique specifications.",
      image: "/images/hero/slide3.jpg",
    },
  ];

  return (
    <HeroSection id="home">
      <VideoBackground autoPlay loop muted playsInline>
        <source src="/videos/hero.mp4" type="video/mp4" />
      </VideoBackground>
      <Overlay />
      <StyledSlider {...sliderSettings} ref={sliderRef}>
        <VideoSlide>
          <YouTubeContainer>
            <YouTubeIframe
              ref={videoRef}
              src="https://www.youtube.com/embed/_NjDxBsMk5M?autoplay=1&mute=1&controls=0&loop=0&enablejsapi=1&rel=0&start=0"
              title="Jupiter & Co Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </YouTubeContainer>
          <Overlay />
          <SlideContent>
            <Title>Watch Our Story</Title>
            <Subtitle>Experience the craftsmanship and dedication behind Jupiter & Co</Subtitle>
            <CTAButton href="#contact">Get in Touch</CTAButton>
          </SlideContent>
        </VideoSlide>
        {slides.map((slide, index) => (
          <Slide key={index} bgImage={slide.image}>
            <SlideContent>
              <Title>{slide.title}</Title>
              <Subtitle>{slide.subtitle}</Subtitle>
              <CTAButton href="#contact">Get in Touch</CTAButton>
            </SlideContent>
          </Slide>
        ))}
      </StyledSlider>
      <ScrollIndicator>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
        </svg>
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero; 